import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Modal from '../../../../commonComponents/modal/Modal';
import translate from '../../../../commonComponents/translate';
import CommissionProp, { SALE_STATUS } from '../../proptype/commissionProp';
import { formatDate } from '../../../../services/dateUtilityFunctions';
import { newFormatPrice } from '../../../../services/utilityFunctions';
import SaleHistory from './SaleHistory';

function SaleModal({ t, onClose, commission }) {
  const {
    sale,
    amount: commissionAmount,
    commission: commissionValue,
    date: commissionDate,
    currency,
  } = commission || {};
  const {
    status,
    saleId,
    amount: amountSale,
    metadata,
    paymentDate: salePaymentDate,
    history,
  } = sale || {};

  const saleIdentifier = saleId || '-';
  const saleStatus = useMemo(() => {
    let translation;
    let badge;
    switch (status) {
      case SALE_STATUS.PAID:
        translation = t('commissions.user.sale.status.paid');
        badge = 'sale-bg-paid';
        break;
      case SALE_STATUS.ON_GOING:
        translation = t('commissions.user.sale.status.on_going');
        badge = 'sale-bg-ongoing';
        break;
      case SALE_STATUS.RECEIVED:
        translation = t('commissions.user.sale.status.received');
        badge = 'sale-bg-received';
        break;
      case SALE_STATUS.CANCELED:
        translation = t('commissions.user.sale.status.canceled');
        badge = 'sale-bg-canceled';
        break;
      case SALE_STATUS.REJECTED:
        translation = t('commissions.user.sale.status.rejected');
        badge = 'sale-bg-canceled';
        break;
      case SALE_STATUS.DISABLED:
        translation = t('commissions.user.sale.status.disabled');
        badge = 'sale-bg-canceled';
        break;
      default:
        translation = '-';
        badge = '';
        break;
    }
    return { translation, badge };
  }, [t, status]);
  const date = formatDate(commissionDate, 'DD MMM YYYY');
  const paymentDate = salePaymentDate
    ? formatDate(salePaymentDate, 'DD MMM YYYY')
    : '-';
  const turnover =
    commissionAmount || amountSale
      ? newFormatPrice(commissionAmount || amountSale, true, currency, true)
      : '-';
  const commissionPrice = commissionValue
    ? newFormatPrice(commissionValue, true, currency, true)
    : '-';

  const listValues = metadata ? Object.keys(metadata) : [];

  const formatListValue = useCallback(
    (field, value) => {
      // This condition is just to prevent this cases from the API, because the frontend will crash
      if (Array.isArray(value) || typeof value === 'object') {
        return '(Custom Object)';
      }
      if (typeof value === 'boolean') {
        return value ? t('yes') : t('no');
      }
      if (typeof value === 'string' && value.includes('_')) {
        return value.replace(/_/g, ' ');
      }
      return value;
    },
    [t],
  );
  return (
    <Modal id="saleModal" onClose={onClose}>
      <div
        className="modal-dialog modal-dialog-centered modal-lg"
        role="document"
      >
        <div className="modal-content">
          <div className="sale-modal-container">
            <div className="sale-modal-table">
              <div className="header">
                <h5 className="modal-title">
                  {t('commission.modal.sale.header')}
                </h5>
              </div>
              <div className="modal-body">
                {/** HEADER */}
                <div className="grid">
                  <div className="item">
                    <div className="id-container">
                      <span className="commission-modal-label">
                        {t('commission.modal.sale.id')}
                      </span>
                      <div
                        className={`commission-modal-value sale-id ${
                          !saleId ? 'empty-sale-id' : ''
                        }`}
                      >
                        <span>{saleIdentifier}</span>
                      </div>
                    </div>
                  </div>
                  <div className="item">
                    <div className="date-container">
                      <span className="commission-modal-label">
                        {t('commission.modal.sale.date')}
                      </span>
                      <div className="commission-modal-value">{date}</div>
                    </div>
                  </div>
                  <div className="item">
                    <div className="turnover-container">
                      <span className="commission-modal-label">
                        {t('commission.modal.sale.turnover')}
                      </span>
                      <div className="commission-modal-value">{turnover}</div>
                    </div>
                  </div>
                  <div className="item">
                    <div className="commission-container">
                      <span className="commission-modal-label">
                        {t('commission.modal.sale.commission')}
                      </span>
                      <div className="commission-modal-value">
                        {commissionPrice}
                      </div>
                    </div>
                  </div>
                </div>
                {/** LIST */}
                {metadata && listValues?.length > 0 && (
                  <ul className="sale-list">
                    {listValues.map(value => (
                      <li key={value}>
                        <div className="commission-modal-label">
                          {t(`commission.modal.sale.metadata.${value}`)}
                        </div>
                        <div className="list-value">
                          {formatListValue(value, metadata[value])}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            <SaleHistory
              history={history}
              onClose={onClose}
              status={status}
              paymentDate={paymentDate}
              saleStatus={saleStatus}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}

SaleModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  commission: PropTypes.shape(CommissionProp.propType).isRequired,
  t: PropTypes.func.isRequired,
};

SaleModal.defaultProps = {};

export default translate(SaleModal);
